import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { listMyTasks, type MyTaskRow } from "@/lib/admin.functions";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

export const Route = createFileRoute("/tasks/")({
  head: () => ({
    meta: [
      { title: "My Tasks - DeckScore" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: TasksIndex,
});

const statusLabel: Record<MyTaskRow["fulfillment_status"], string> = {
  unassigned: "Unassigned",
  assigned: "Assigned",
  in_progress: "In progress",
  completed: "Completed",
};

function StatusBadge({ status }: { status: MyTaskRow["fulfillment_status"] }) {
  const tone =
    status === "completed"
      ? "bg-[#DDF0DA] text-[#2E5B2E]"
      : status === "in_progress"
        ? "bg-[#FDE9C7] text-[#8A5A16]"
        : status === "assigned"
          ? "bg-accent-soft text-accent-ink"
          : "bg-surface-sunken text-ink-tertiary";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${tone}`}
    >
      {statusLabel[status]}
    </span>
  );
}

function DeadlineCell({
  paidAt,
  createdAt,
  completed,
}: {
  paidAt: string | null;
  createdAt: string | null;
  completed: boolean;
}) {
  const base = paidAt ?? createdAt;
  if (!base || completed) return <span className="text-ink-tertiary">-</span>;
  const deadline = new Date(base).getTime() + 48 * 60 * 60 * 1000;
  const now = Date.now();
  const overdue = now > deadline;
  const ms = Math.abs(deadline - now);
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  if (overdue) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-red/10 text-red border border-red/30">
        Overdue {h}h {m}m
      </span>
    );
  }
  return (
    <span className="text-ink-secondary text-[12px]">
      {h}h {m}m left
    </span>
  );
}

function isOverdue(t: MyTaskRow): boolean {
  if (t.fulfillment_status === "completed") return false;
  const base = t.paid_at ?? t.created_at;
  if (!base) return false;
  return Date.now() > new Date(base).getTime() + 48 * 60 * 60 * 1000;
}

function TasksIndex() {
  const run = useServerFn(listMyTasks);
  const [tasks, setTasks] = useState<MyTaskRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<"all" | "assigned" | "in_progress" | "completed">("all");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [sortBy, setSortBy] = useState<"newest" | "oldest" | "deadline">("newest");

  useEffect(() => {
    (async () => {
      try {
        const { tasks } = await run();
        setTasks(tasks);
      } finally {
        setLoading(false);
      }
    })();
  }, [run]);

  const dateOf = (t: MyTaskRow): number => {
    const iso = t.paid_at ?? t.created_at;
    const ms = iso ? new Date(iso).getTime() : NaN;
    return isNaN(ms) ? 0 : ms;
  };
  const deadlineOf = (t: MyTaskRow): number => {
    if (t.fulfillment_status === "completed") return Number.MAX_SAFE_INTEGER;
    const base = dateOf(t);
    return base === 0 ? Number.MAX_SAFE_INTEGER : base + 48 * 60 * 60 * 1000;
  };

  const visible = tasks
    .filter((t) => (statusFilter === "all" ? true : t.fulfillment_status === statusFilter))
    .filter((t) => (overdueOnly ? isOverdue(t) : true))
    .slice()
    .sort((a, b) => {
      if (sortBy === "deadline") return deadlineOf(a) - deadlineOf(b);
      if (sortBy === "oldest") return dateOf(a) - dateOf(b);
      return dateOf(b) - dateOf(a);
    });

  return (
    <>
      <div className="mb-6">
        <p className="font-script text-accent text-[22px] leading-none">your queue</p>
        <h1 className="font-display text-[28px] font-semibold mt-2">My tasks</h1>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
          <SelectTrigger className="h-9 text-[13px] w-[170px]">
            <SelectValue placeholder="Fulfillment" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All fulfillment</SelectItem>
            <SelectItem value="assigned">Assigned</SelectItem>
            <SelectItem value="in_progress">In progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={(v) => setSortBy(v as typeof sortBy)}>
          <SelectTrigger className="h-9 text-[13px] w-[180px]">
            <SelectValue placeholder="Sort" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest first</SelectItem>
            <SelectItem value="oldest">Oldest first</SelectItem>
            <SelectItem value="deadline">Deadline soonest</SelectItem>
          </SelectContent>
        </Select>
        <label className="inline-flex items-center gap-2 text-[13px] cursor-pointer ml-1">
          <input
            type="checkbox"
            checked={overdueOnly}
            onChange={(e) => setOverdueOnly(e.target.checked)}
            className="accent-red"
          />
          Overdue only
        </label>
      </div>

      <div className="bg-surface border border-line rounded-2xl overflow-hidden">
        <table className="w-full text-[14px]">
          <thead className="bg-surface-sunken text-ink-secondary">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Company</th>
              <th className="text-left px-4 py-3 font-medium">Date</th>
              <th className="text-left px-4 py-3 font-medium">Programs</th>
              <th className="text-left px-4 py-3 font-medium">48h SLA</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-ink-tertiary">
                  Loading…
                </td>
              </tr>
            ) : visible.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-ink-tertiary">
                  {tasks.length === 0 ? "No tasks assigned to you yet." : "No tasks match these filters."}
                </td>
              </tr>
            ) : (
              visible.map((t) => (
                <tr
                  key={t.id}
                  className="border-t border-line hover:bg-surface-sunken/40 transition"
                >
                  <td className="px-4 py-3 font-medium">
                    <Link
                      to="/tasks/$submissionId"
                      params={{ submissionId: t.id }}
                      className="hover:underline"
                    >
                      {t.company}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-ink-secondary">
                    {t.created_at ? new Date(t.created_at).toLocaleDateString() : "-"}
                  </td>
                  <td className="px-4 py-3 text-ink-secondary">
                    {t.targets.total === 0 ? (
                      <span className="text-ink-tertiary">-</span>
                    ) : (
                      <span>
                        {t.targets.submitted}✓ / {t.targets.pending} pending /{" "}
                        {t.targets.not_a_fit}✗
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <DeadlineCell
                      paidAt={t.paid_at}
                      createdAt={t.created_at}
                      completed={t.fulfillment_status === "completed"}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={t.fulfillment_status} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

